import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useClientFilter } from '@/hooks/useClientFilter';

export interface ReportTicket {
  id: string;
  numero: string;
  titulo: string;
  tipo: 'incidente' | 'requisicao' | 'problema' | 'mudanca';
  tipoLabel: string;
  status: string;
  prioridade: string;
  urgencia: string;
  solicitante: string;
  cliente: string;
  grupo: string;
  atendente: string;
  categoria: string;
  canal_origem: string;
  data_abertura: string;
  data_resolucao: string | null;
  data_fechamento: string | null;
  sla_status: string;
}

interface UseReportTicketListParams {
  startDate: Date;
  endDate: Date;
  ticketTypes: string[];
  priority: string;
}

export const useReportTicketList = ({ startDate, endDate, ticketTypes, priority }: UseReportTicketListParams) => {
  const { user } = useAuth();
  const { getClientFilter } = useClientFilter();

  return useQuery({
    queryKey: ['report-ticket-list', startDate.toISOString(), endDate.toISOString(), ticketTypes, priority, getClientFilter()],
    queryFn: async () => {
      const tickets: ReportTicket[] = [];
      const start = startDate.toISOString();
      const end = endDate.toISOString();
      const clientId = getClientFilter();

      const withClient = (query: any, field = 'client_id') => {
        return clientId ? query.eq(field, clientId) : query;
      };

      // Fetch solicitacoes (incidentes + requisicoes)
      if (ticketTypes.includes('incidente') || ticketTypes.includes('requisicao')) {
        let query = supabase
          .from('solicitacoes')
          .select(`
            id, numero, titulo, tipo, status, prioridade, urgencia, canal_origem,
            criado_em, data_resolucao, data_fechamento, data_limite_resolucao,
            solicitante:users!solicitacoes_solicitante_id_fkey(name),
            cliente:clients(name),
            grupo_responsavel:groups(name),
            atendente:users!solicitacoes_atendente_id_fkey(name),
            categoria:categorias_servico(nome)
          `)
          .gte('criado_em', start)
          .lte('criado_em', end);

        const tipos: string[] = [];
        if (ticketTypes.includes('incidente')) tipos.push('incidente');
        if (ticketTypes.includes('requisicao')) tipos.push('requisicao');
        if (tipos.length === 1) {
          query = query.eq('tipo', tipos[0]);
        } else if (tipos.length === 2) {
          query = query.in('tipo', tipos);
        }

        if (priority !== 'all') {
          query = query.eq('prioridade', priority);
        }

        query = withClient(query);

        const { data } = await query.order('criado_em', { ascending: false });

        if (data) {
          for (const item of data) {
            const sol = item as any;
            const now = new Date();
            const deadline = sol.data_limite_resolucao ? new Date(sol.data_limite_resolucao) : null;
            let slaStatus = 'N/A';
            if (deadline) {
              if (['resolvida', 'fechada'].includes(sol.status)) {
                const resolved = sol.data_resolucao ? new Date(sol.data_resolucao) : now;
                slaStatus = resolved <= deadline ? 'Cumprido' : 'Violado';
              } else {
                slaStatus = now <= deadline ? 'Dentro do prazo' : 'Violado';
              }
            }

            tickets.push({
              id: sol.id,
              numero: sol.numero || '-',
              titulo: sol.titulo || '',
              tipo: sol.tipo || 'requisicao',
              tipoLabel: sol.tipo === 'incidente' ? 'Incidente' : 'Requisição',
              status: sol.status || '',
              prioridade: sol.prioridade || '',
              urgencia: sol.urgencia || '',
              solicitante: sol.solicitante?.name || '-',
              cliente: sol.cliente?.name || '-',
              grupo: sol.grupo_responsavel?.name || '-',
              atendente: sol.atendente?.name || '-',
              categoria: sol.categoria?.nome || '-',
              canal_origem: sol.canal_origem || '-',
              data_abertura: sol.criado_em || '',
              data_resolucao: sol.data_resolucao,
              data_fechamento: sol.data_fechamento,
              sla_status: slaStatus,
            });
          }
        }
      }

      // Fetch problemas
      if (ticketTypes.includes('problema')) {
        let query = supabase
          .from('problemas' as any)
          .select(`
            id, numero, titulo, status, prioridade, urgencia,
            criado_em, data_resolucao, client_id
          `)
          .gte('criado_em', start)
          .lte('criado_em', end);

        if (priority !== 'all') {
          query = query.eq('prioridade', priority);
        }

        query = withClient(query);

        const { data } = await query.order('criado_em', { ascending: false });

        if (data) {
          for (const item of data as any[]) {
            tickets.push({
              id: item.id,
              numero: item.numero || '-',
              titulo: item.titulo || '',
              tipo: 'problema',
              tipoLabel: 'Problema',
              status: item.status || '',
              prioridade: item.prioridade || '',
              urgencia: item.urgencia || '',
              solicitante: '-',
              cliente: '-',
              grupo: '-',
              atendente: '-',
              categoria: '-',
              canal_origem: '-',
              data_abertura: item.criado_em || '',
              data_resolucao: item.data_resolucao,
              data_fechamento: null,
              sla_status: 'N/A',
            });
          }
        }
      }

      // Fetch mudancas
      if (ticketTypes.includes('mudanca')) {
        let query = supabase
          .from('solicitacoes')
          .select(`
            id, numero, titulo, status, prioridade, urgencia, canal_origem,
            criado_em, data_resolucao, data_fechamento, data_limite_resolucao,
            solicitante:users!solicitacoes_solicitante_id_fkey(name),
            cliente:clients(name),
            grupo_responsavel:groups(name),
            atendente:users!solicitacoes_atendente_id_fkey(name),
            categoria:categorias_servico(nome)
          `)
          .eq('tipo', 'mudanca')
          .gte('criado_em', start)
          .lte('criado_em', end);

        if (priority !== 'all') {
          query = query.eq('prioridade', priority);
        }

        query = withClient(query);

        const { data } = await query.order('criado_em', { ascending: false });

        if (data) {
          for (const item of data) {
            const sol = item as any;
            tickets.push({
              id: sol.id,
              numero: sol.numero || '-',
              titulo: sol.titulo || '',
              tipo: 'mudanca',
              tipoLabel: 'Mudança',
              status: sol.status || '',
              prioridade: sol.prioridade || '',
              urgencia: sol.urgencia || '',
              solicitante: sol.solicitante?.name || '-',
              cliente: sol.cliente?.name || '-',
              grupo: sol.grupo_responsavel?.name || '-',
              atendente: sol.atendente?.name || '-',
              categoria: sol.categoria?.nome || '-',
              canal_origem: sol.canal_origem || '-',
              data_abertura: sol.criado_em || '',
              data_resolucao: sol.data_resolucao,
              data_fechamento: sol.data_fechamento,
              sla_status: 'N/A',
            });
          }
        }
      }

      // Sort by date descending
      tickets.sort((a, b) => new Date(b.data_abertura).getTime() - new Date(a.data_abertura).getTime());

      return tickets;
    },
    enabled: !!user,
  });
};
